import { Box, Link, Typography } from '@mui/material'
import { FC, Fragment } from 'react'
import {
  CarePlanItem,
  CategorizedMeasureCollection,
  MeasureCollectionMeasure,
  MeasureStatus,
} from './carePlanTypes'
import WarningIcon from '@mui/icons-material/Warning'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useNavigate } from 'react-router'
import Article from '@mui/icons-material/Article'
import Timelapse from '@mui/icons-material/Timelapse'
import { logger } from '~/shared/lib/logger'
import { YELLOW } from '~/shared/lib/theme'

type MeasureReportApi = NonNullable<MeasureCollectionMeasure['measure_reports']>[number]
type MeasureReportCaseApi = NonNullable<MeasureReportApi['cases']>[number]

const MEASURE_STATUS_SX = { fontSize: '1.2rem', mb: '8px' }

const CARE_GAP_STATUS_BACKGROUND: Record<string, string> = {
  Overdue: '#FFE6E5',
  'Due Soon': YELLOW[100],
}

const CaseBasedCarePlanContent: FC<{
  carePlan: CarePlanItem
  categorizedMeasureCollection: CategorizedMeasureCollection
}> = props => {
  const { carePlan, categorizedMeasureCollection } = props

  const navigate = useNavigate()

  const orderedMeasureStatuses: MeasureStatus[] = ['Outstanding', 'Complete']

  const openLinkedCase = (e: React.MouseEvent<HTMLElement>) => {
    const caseId = e.currentTarget.getAttribute('data-case-id')

    if (caseId) {
      const path = location.pathname
      const basePath = path.substring(0, path.lastIndexOf('/'))
      void navigate(`${basePath}/todos?caseId=${caseId}`, { replace: true })
    } else {
      logger.warn('[CarePlan] case not found', caseId)
    }
  }

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'Overdue':
        return <WarningIcon color="error" fontSize="small"></WarningIcon>
      case 'Due Soon':
        return <Timelapse color="warning" fontSize="small"></Timelapse>
      default:
        return <CheckBoxIcon color="success" fontSize="small"></CheckBoxIcon>
    }
  }

  return (
    <>
      <Box sx={{ ml: 3 }}>
        {Object.keys(categorizedMeasureCollection).length > 0 ? (
          orderedMeasureStatuses.map(measureStatus => {
            return (
              <Box key={measureStatus}>
                {measureStatus in categorizedMeasureCollection ? (
                  <>
                    <Typography sx={MEASURE_STATUS_SX}>{measureStatus}</Typography>
                    {/* border box */}
                    <Box>
                      {categorizedMeasureCollection[measureStatus].map((measureDetails, i) => {
                        return (
                          <Box key={i}>
                            {measureDetails.measure_reports.map((report: MeasureReportApi) => {
                              return (
                                <Box
                                  key={report.id}
                                  sx={{
                                    width: '100%',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: 1,
                                    marginBottom: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'space-between',
                                      padding: 2,
                                    }}
                                  >
                                    {/* first column */}
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        flexWrap: 'wrap',
                                        width: '40%',
                                      }}
                                    >
                                      <Box sx={{ width: '100%' }}>
                                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                                          {measureDetails.measure.name}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'inline-flex',
                                            justifyContent: 'flex-start',
                                            fontSize: '1.2rem',
                                          }}
                                        >
                                          {(report.cases ?? []).map(
                                            (measureCase: MeasureReportCaseApi) => {
                                              return (
                                                <Fragment key={measureCase.id}>
                                                  <Link
                                                    color="inherit"
                                                    data-case-id={measureCase.id}
                                                    onClick={openLinkedCase}
                                                    style={{
                                                      cursor: 'pointer',
                                                    }}
                                                  >
                                                    {`Case`}
                                                  </Link>
                                                  <Typography sx={{ fontSize: '1.2rem' }}>
                                                    {`: ` + measureCase.status}
                                                  </Typography>
                                                </Fragment>
                                              )
                                            },
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                    {/* second column */}
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        flexWrap: 'wrap',
                                        width: '30%',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          flexDirection: 'column',
                                          display: 'flex',
                                          width: '100%',
                                        }}
                                      >
                                        <Typography sx={{ fontSize: '1.2rem' }}>
                                          {report.last_action_date || report.last_action
                                            ? `Last: `
                                            : ''}
                                          {report.last_action_date
                                            ? report.last_action_date + `, `
                                            : ''}
                                          {report.last_action ? report.last_action : ''}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.2rem' }}>
                                          {report.next_action_date
                                            ? `Satisfied Until: ` + report.next_action_date
                                            : ''}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {/* third column */}
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        flexWrap: 'wrap',
                                        width: '30%',
                                        paddingLeft: 12,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          justifyContent: 'flex-start',
                                          px: '8px',
                                          py: '0.5px',
                                          borderRadius: '16px',
                                          height: '30px',
                                          alignItems: 'center',
                                          backgroundColor:
                                            CARE_GAP_STATUS_BACKGROUND[
                                              report.care_gap_status ?? ''
                                            ] ?? '#E5F9F4',
                                        }}
                                      >
                                        {renderStatusIcon(report.care_gap_status)}
                                        <Typography
                                          sx={{
                                            marginLeft: 0.5,
                                            fontWeight: '500',
                                            fontSize: '1.4rem',
                                          }}
                                        >
                                          {report.care_gap_status}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              )
                            })}
                          </Box>
                        )
                      })}
                    </Box>
                  </>
                ) : null}
              </Box>
            )
          })
        ) : (
          <Typography sx={MEASURE_STATUS_SX}>
            {'Will be ready for review 24 hours after the first visit'}
          </Typography>
        )}
        {carePlan.status == 'active' ? (
          <>
            <Box>
              {carePlan.notes ? (
                <>
                  <Typography
                    sx={{
                      fontSize: '1.2rem',
                      color: 'navy.500',
                      marginBottom: 1,
                      marginTop: 4,
                    }}
                  >
                    {'From Your Clinician'}
                  </Typography>
                  <Typography sx={{ fontSize: '1.4rem', whiteSpace: 'pre-wrap' }}>
                    {carePlan.notes}
                  </Typography>
                </>
              ) : null}
            </Box>
            <Box>
              {(carePlan.care_plan_education_resources ?? []).length > 0 ? (
                <Typography
                  sx={{
                    fontSize: '1.2rem',
                    color: 'navy.500',
                    marginBottom: 1,
                    marginTop: 4,
                  }}
                >
                  {'Resources'}
                </Typography>
              ) : null}
              {(carePlan.care_plan_education_resources ?? []).map(resource => {
                return (
                  <Box
                    key={resource.education_resource.title}
                    sx={{ alignSelf: 'center', display: 'flex', marginBottom: 1 }}
                  >
                    <Article color="inherit" sx={{ fontSize: '2rem', marginRight: 1 }} />
                    <Typography sx={{ fontSize: '1.4rem' }}>
                      {resource.education_resource.title}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
          </>
        ) : null}
      </Box>
    </>
  )
}

export default CaseBasedCarePlanContent
